import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useEffect, useMemo, useState } from 'react';
import { shape, string, arrayOf, func, bool, number, oneOf } from 'prop-types';
import { Icon } from '@magalu/stereo-ui-icons';
import { getAvailability, getVariation, sortByAvailable, sortBySelected } from '../../utils';
import Flex from '../../atoms/Flex';
import Image from '../../atoms/ResponsiveImage';
import AttributeBase from '../AttributeBase';
import AttributeRadio from '../AttributeRadio';
import { Buttoned } from './attribute-color.styles';

const Smoothable = ({
  onClick,
  show
}) => {
  return show && /*#__PURE__*/React.createElement(Flex, {
    "data-testid": "attribute-color-more",
    position: "absolute",
    justifyContent: "center",
    background: "linear-gradient(0deg, #ffffff, transparent 80%)",
    color: "text.primary",
    fontWeight: "bold",
    cursor: "pointer",
    width: 1,
    bottom: 0,
    height: 36,
    padding: 1,
    onClick: onClick
  }, "ver mais cores");
};

process.env.NODE_ENV !== "production" ? Smoothable.propTypes = {
  show: bool.isRequired,
  onClick: func.isRequired
} : void 0;

const Buttonable = ({
  attributesLength,
  onClick,
  show,
  style
}) => {
  return show && /*#__PURE__*/React.createElement(Buttoned, _extends({
    "data-testid": "attribute-color-more",
    fontSize: 4,
    borderRadius: "default",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderWidth: 1,
    borderColor: "#e5e5e5",
    borderStyle: "solid",
    onClick: onClick
  }, style), "+ ", attributesLength);
};

process.env.NODE_ENV !== "production" ? Buttonable.propTypes = {
  attributesLength: number.isRequired,
  onClick: func.isRequired,
  show: bool.isRequired,
  style: shape({
    width: string,
    shouldHover: bool,
    height: string,
    bg: string,
    color: string
  })
} : void 0;
const componentVariants = {
  smooth: Smoothable,
  button: Buttonable
};

const AttributeColor = _ref => {
  let {
    attribute,
    variations,
    onChange,
    onVariationClick,
    title,
    alignment,
    minLength,
    variant,
    showTitle,
    style
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["attribute", "variations", "onChange", "onVariationClick", "title", "alignment", "minLength", "variant", "showTitle", "style"]);

  const verifiedVariant = ['smooth', 'button'].includes(variant) ? variant : 'smooth';
  const ShowMoreComponent = componentVariants == null ? void 0 : componentVariants[verifiedVariant];
  const [smoothable, setSmoothable] = useState(verifiedVariant === 'smooth');
  const [buttonable, setbuttonable] = useState(verifiedVariant === 'button');
  const [attrValues, setAttrValues] = useState([]);
  const sorted = sortByAvailable(attribute)(variations);
  const sortedValues = useMemo(() => {
    var _sortBySelected;

    const values = sorted.reduce((acc, value) => [...acc, Object.assign({}, getVariation(variations, value), {
      selected: buttonable && (attribute == null ? void 0 : attribute.selected) && (attribute == null ? void 0 : attribute.current) === value
    })], []);
    return (_sortBySelected = sortBySelected(values)) == null ? void 0 : _sortBySelected.map(variation => variation.value);
  }, [attribute, variations]);
  const hasShowMore = useMemo(() => {
    return sortedValues.length > minLength;
  }, []);
  useEffect(() => {
    if (sortedValues.length > minLength && verifiedVariant === 'button') {
      return setAttrValues(sortedValues.slice(0, minLength - 1));
    }

    return setAttrValues(sorted);
  }, [sortedValues]);

  const handleOnClick = () => {
    if (smoothable) {
      return setSmoothable(!smoothable);
    }

    setbuttonable(!buttonable);
    return setAttrValues(attrValues);
  };

  return /*#__PURE__*/React.createElement(AttributeBase, _extends({}, attribute, {
    smoothable: hasShowMore && smoothable,
    alignment: alignment,
    showTitle: showTitle
  }, props), attrValues == null ? void 0 : attrValues.map((value, index) => {
    const {
      image,
      path
    } = getVariation(variations, value) || {};
    const availability = getAvailability(variations, value);
    return /*#__PURE__*/React.createElement(AttributeRadio, {
      key: `${value}-${index}`,
      value: value,
      type: attribute.type,
      path: path,
      checked: attribute.selected && attribute.current === value,
      disabled: !availability,
      onChange: onChange,
      onVariationClick: onVariationClick,
      style: style
    }, image ? /*#__PURE__*/React.createElement(Image, {
      alt: `${title} ${value}`,
      title: value,
      src: image,
      width: 50,
      height: 50
    }) : /*#__PURE__*/React.createElement(Icon, {
      name: "Camera",
      width: 29,
      height: 25,
      color: "lightGray"
    }));
  }), hasShowMore && /*#__PURE__*/React.createElement(ShowMoreComponent, {
    show: smoothable || buttonable,
    attributesLength: attribute.values.length - (minLength - 1),
    style: style,
    onClick: showTitle ? handleOnClick : onChange
  }));
};

AttributeColor.defaultProps = {
  attribute: {},
  disabled: false,
  variations: [],
  title: '',
  alignment: '',
  minLength: 5,
  variant: 'smooth',
  showTitle: true,
  onVariationClick: null,
  style: {
    width: '70px',
    height: '70px',
    bg: 'background.base',
    fontWeight: 'bold',
    color: 'text.base'
  }
};
process.env.NODE_ENV !== "production" ? AttributeColor.propTypes = {
  attribute: shape({
    label: string,
    type: string,
    current: string,
    selected: bool,
    values: arrayOf(string)
  }),
  variations: arrayOf(shape({
    image: string,
    available: bool,
    type: string,
    value: string,
    id: string
  })),
  title: string,
  onVariationClick: func,
  onChange: func,
  alignment: string,
  minLength: number,
  showTitle: bool,
  variant: oneOf(['button', 'smooth']),
  style: shape({
    width: string,
    height: string,
    bg: string,
    color: string
  })
} : void 0;
export default AttributeColor;